import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">分类未找到</h2>
            <p className="text-sm text-foreground-600 mb-4">请检查分类链接是否正确</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = getCategoryBreadcrumb(slug);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `鬼父2${category.name} - 鬼父2解析站`,
    description: `鬼父2${category.name}分类下的全部深度解析文章，${category.description}`,
    url: `https://www.guifu2-analysis.com/category/${slug}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-8 pb-8">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex items-center gap-4 mt-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  鬼父2{category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-600">该分类下暂无文章，敬请期待</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer border border-background-200/60 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="h-[200px] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {article.hot && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-primary-100 text-primary-700">
                          热门
                        </span>
                      )}
                    </div>
                    <h2 className="font-heading text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.publishDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {article.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}